/**
 * An Order and wait PO's order with the supplier (BLU-1501): what happened, and what
 * a person can do about it. The page's other cards say where the box is; this one
 * says whether the supplier has the order at all.
 *
 *   Placing       Nothing to do. Over 10 minutes unsent: Put back
 *   Not placed    Put back, I ordered it on the website
 *   Checking      Check again, I found it (types the supplier's order number), and
 *                 for org settings only It is not there (types the PO number),
 *                 which opens after a clean read. The server says when.
 *   Placed        The supplier's order number
 *   Mode off      No check runs and none can be asked for: Check again, I found it and
 *                 the promise that we keep checking all go. Arrived still settles it.
 *   The rest      Nothing here: the PO's own status says it
 *
 * The buttons show for the permissions the server holds (RBAC four layers), and the
 * server refuses whatever this page shows out of date.
 */
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { isSafeSupplierOrderNumber, type SupplierApiOrderView } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NoteLine } from '@/components/shared/NoteLine';
import { useApiOrderActions } from '../hooks/useOrderAndWait';

const SHOWN = new Set(['queued', 'posting', 'refused', 'unknown', 'placed']);

/** Why it was refused, in words. Reason codes never reach the screen. */
function refusedKey(code: string | null): string {
  if (!code) return 'oaw.api.refused';
  if (code === 'login_changed') return 'oaw.api.refusedLoginChanged';
  if (code.startsWith('login_') || code === 'needs_login' || code === 'no_login' || code === 'no_account') {
    return code === 'login_unavailable' ? 'oaw.api.refusedDown' : 'oaw.api.refusedLogin';
  }
  if (code === 'not_sent') return 'oaw.api.refusedDown';
  if (code === 'post_429') return 'oaw.api.refusedBusy';
  if (code === 'ordering_off' || code === 'orders_off') return 'oaw.api.refusedOff';
  return 'oaw.api.refused';
}

interface ApiOrderCardProps {
  apiOrder: SupplierApiOrderView;
  poNumber: string;
  supplierName: string | null;
  canManage: boolean;
  canSayNotThere: boolean;
}

type OpenDialog = 'putBack' | 'found' | 'notThere' | null;

export function ApiOrderCard({ apiOrder, poNumber, supplierName, canManage, canSayNotThere }: ApiOrderCardProps) {
  const { t } = useTranslation('orders');
  const supplier = supplierName?.trim() || t('oaw.yourSupplier');
  const actions = useApiOrderActions({ poId: apiOrder.poId, poNumber, supplierName });
  const [open, setOpen] = useState<OpenDialog>(null);
  const { status, reference, supplierOrderId, checksRun } = apiOrder;
  if (!SHOWN.has(status)) return null;

  const busy = actions.pending !== null;
  const ambiguous = apiOrder.lastErrorCode === 'reference_ambiguous';
  const loginChanged = apiOrder.lastErrorCode === 'login_changed';
  const unknownKey = !checksRun
    ? 'oaw.api.unknownNoChecks'
    : loginChanged
      ? 'oaw.api.loginChanged'
      : ambiguous
        ? 'oaw.api.ambiguous'
        : 'oaw.api.unknown';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t('oaw.api.title', { supplier })}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {(status === 'queued' || status === 'posting') && (
          <p role="status">{apiOrder.canPutBack ? t('oaw.api.placingStuck') : t('oaw.api.placing', { supplier })}</p>
        )}
        {status === 'refused' && <p role="status">{t(refusedKey(apiOrder.lastErrorCode), { supplier })}</p>}
        {status === 'unknown' && (
          <p role="status">{t(unknownKey, { supplier, reference })}</p>
        )}
        {status === 'placed' && (
          <p>
            <span className="text-muted-foreground">{t('oaw.api.placed', { supplier })}: </span>
            <span className="break-all font-mono">{supplierOrderId}</span>
          </p>
        )}

        {canManage && (
          <div className="flex flex-wrap gap-2">
            {apiOrder.canPutBack && (
              <Button type="button" variant="outline" disabled={busy} onClick={() => setOpen('putBack')}>
                {t('oaw.api.putBack')}
              </Button>
            )}
            {status === 'refused' && (
              <Button type="button" variant="outline" disabled={busy} onClick={() => void actions.website()}>
                {t('oaw.api.website')}
              </Button>
            )}
            {status === 'unknown' && checksRun && (
              <>
                <Button type="button" variant="outline" disabled={busy} onClick={() => void actions.checkAgain()}>
                  {t('oaw.api.checkAgain')}
                </Button>
                <Button type="button" variant="outline" disabled={busy} onClick={() => setOpen('found')}>
                  {t('oaw.api.found')}
                </Button>
              </>
            )}
          </div>
        )}
        {canSayNotThere && status === 'unknown' && (
          apiOrder.canSayNotThere ? (
            <Button type="button" variant="outline" disabled={busy} onClick={() => setOpen('notThere')}>
              {t('oaw.api.notThere')}
            </Button>
          ) : (
            checksRun && !ambiguous && !loginChanged && <NoteLine as="p">{t('oaw.api.notThereWait')}</NoteLine>
          )
        )}
      </CardContent>

      <ConfirmDialog
        open={open === 'putBack'}
        onClose={() => setOpen(null)}
        title={t('oaw.api.putBackTitle', { po: poNumber })}
        body={t('oaw.api.putBackBody', { po: poNumber })}
        confirm={t('oaw.api.putBack')}
        pending={actions.pending === 'putBack'}
        onConfirm={() => actions.putBack()}
      />
      <ConfirmDialog
        open={open === 'found'}
        onClose={() => setOpen(null)}
        title={t('oaw.api.foundTitle', { supplier })}
        body={t('oaw.api.foundBody', { supplier, reference })}
        confirm={t('oaw.api.foundSave')}
        pending={actions.pending === 'found'}
        field={{ label: t('oaw.api.foundLabel'), valid: isSafeSupplierOrderNumber }}
        onConfirm={(value) => actions.found(value)}
      />
      <ConfirmDialog
        open={open === 'notThere'}
        onClose={() => setOpen(null)}
        title={t('oaw.api.notThereTitle', { po: poNumber, supplier })}
        body={t('oaw.api.notThereBody', { po: poNumber, supplier })}
        confirm={t('oaw.api.notThereSave')}
        destructive
        pending={actions.pending === 'notThere'}
        field={{ label: t('oaw.api.notThereLabel'), valid: (value) => value === poNumber.trim() }}
        onConfirm={(value) => actions.notThere(value)}
      />
    </Card>
  );
}

function ConfirmDialog({
  open,
  onClose,
  title,
  body,
  confirm,
  destructive,
  pending,
  field,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  body: string;
  confirm: string;
  destructive?: boolean;
  pending: boolean;
  field?: { label: string; valid: (value: string) => boolean };
  onConfirm: (value: string) => Promise<boolean>;
}) {
  const { t } = useTranslation('orders');
  const inputId = useId();
  const [value, setValue] = useState('');
  const trimmed = value.trim();
  const ready = !field || field.valid(trimmed);

  const close = () => {
    setValue('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next || pending ? undefined : close())}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        {field && (
          <div className="space-y-1.5">
            <Label htmlFor={inputId}>{field.label}</Label>
            <Input id={inputId} value={value} autoComplete="off" onChange={(event) => setValue(event.target.value)} disabled={pending} />
          </div>
        )}
        <DialogFooter className="sm:flex-wrap sm:gap-y-2">
          <Button type="button" variant="outline" onClick={close} disabled={pending}>
            {t('oaw.dialog.back')}
          </Button>
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'default'}
            disabled={pending || !ready}
            aria-busy={pending || undefined}
            onClick={() =>
              void onConfirm(trimmed).then((done) => {
                if (done) close();
              })
            }
            className="gap-1.5"
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
